import React, { useEffect, useRef, useState } from 'react';
import { scrollListToTopAfterPaint } from '../../../utils/scrollListToTop';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Project } from '../../../types/Project';
import { projectService, ProjectListResponse, ProjectFilters } from '../../../services/projectService';
import ProjectModal from '../components/ProjectModal';
import ProjectFiltersComponent from '../components/ProjectFilters';
import CancelProjectConfirmationModal from '../components/CancelProjectConfirmationModal';
import ServerPagination from '../../../components/ServerPagination';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { triggerBackendTest, triggerUnhandledCrash } from '../../../utils/bugsnagTest';
import { formatDateForDisplayUTC } from '../../../utils/dateUtils';
import InfoTooltip from '../../../components/InfoTooltip';
import { formatProjectStatus } from '../components/ProjectFilters';
import {
  Alert,
  Button,
  ClampText,
  ListPage,
  Spinner,
  PageHeader,
  RowActions,
  RowExpandToggle,
  EXPAND_COL_WIDTH,
  ACTIONS_COL_WIDTH,
  DataTable,
  StatusBadge,
  Badge,
  IdLink,
  IdText,
  ServiceAreaPill,
  EmptyValue,
  type DataTableColumn,
} from '@/components/ui';
import { describeActionError } from '@/utils/loadError';
import { usePersistedListState } from '../../../hooks/usePersistedListState';

const SERVICE_AREAS = ['16', '17', '22', '25'];
const DEFAULT_PAGE_SIZE = 20;

function parseProjectsListParams(searchParams: URLSearchParams) {
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10);
  const searchFromUrl = searchParams.get('search') || '';
  const sortByUrl = searchParams.get('sortBy') || undefined;
  const sortOrderUrl =
    searchParams.get('sortOrder') === 'asc'
      ? 'asc'
      : searchParams.get('sortOrder') === 'desc'
        ? 'desc'
        : undefined;
  const filtersFromUrl: ProjectFilters = {
    serviceArea: searchParams.get('serviceArea') || '',
    status: searchParams.get('status') || '',
    type: searchParams.get('type') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    search: searchFromUrl,
    sortBy: sortByUrl,
    sortOrder: sortByUrl ? sortOrderUrl : undefined,
  };
  return { page, pageSize, filters: filtersFromUrl, searchFromUrl };
}

function buildProjectsListSearch(page: number, pageSize: number, filters: ProjectFilters): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });
  return params.toString();
}

const ProjectsView: React.FC = () => {
  // Helper function to safely format date for display
  const formatDateForDisplay = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return 'Not set';
    
    // Handle empty strings
    if (typeof dateValue === 'string' && dateValue.trim() === '') {
      return 'Not set';
    }
    
    // Project dates are stored at UTC midnight. Formatting them in local time renders the
    // previous day in every timezone behind UTC — i.e. everywhere this app is used — so
    // format in UTC via the shared helper.
    const formatted = formatDateForDisplayUTC(dateValue);
    return formatted === 'Unknown' ? 'Invalid Date' : formatted;
  };

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [isCancelProjectModalVisible, setIsCancelProjectModalVisible] = useState(false);
  const [projectToCancel, setProjectToCancel] = useState<Project | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [filters, setFilters] = useState<ProjectFilters>({
    serviceArea: '',
    status: '',
    startDate: '',
    endDate: '',
    type: '',
    search: ''
  });
  const [pendingFilters, setPendingFilters] = useState<ProjectFilters>({
    serviceArea: '',
    status: '',
    startDate: '',
    endDate: '',
    type: '',
    search: ''
  });
  const [filterOptions, setFilterOptions] = useState<{
    types: string[];
    statuses: string[];
    serviceAreas: { id: number; name: string }[];
  }>({
    types: [],
    statuses: [],
    serviceAreas: []
  });
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);
  // A failed cancel is shown inline above the list (STATE-06): the cancel dialog has already closed.
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [projectTasks, setProjectTasks] = useState<Record<number, { tasks: any[]; loading: boolean; error: string | null; totalCount?: number; hasMore?: boolean }>>({});
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canEditProject = hasPermission('project:edit');
  const canCloseProject = hasPermission('project:close');
  const canCreateProject = hasPermission('project:create');
  const canCancelProject = hasPermission('project:cancel');
  const listScrollRef = useRef<HTMLDivElement>(null);
  const fetchRequestIdRef = useRef(0);
  const pendingScrollAfterCreateRef = useRef(false);

  /**
   * The URL is the source of truth here. To keep filters, search, sort and page for the sign-in
   * (V2-024, FLT-09) the query string is remembered, and opening the page with a bare URL (the
   * nav link) puts it back instead of fetching the defaults first.
   */
  const [savedQuery, setSavedQuery] = usePersistedListState('projects', 'query', '');
  const restoringRef = useRef(searchParams.toString() === '' && savedQuery !== '');

  // URL is the source of truth: sync UI state and fetch in one effect to avoid stale page-1 races.
  useEffect(() => {
    if (restoringRef.current) {
      restoringRef.current = false;
      if (searchParams.toString() === '') {
        setSearchParams(new URLSearchParams(savedQuery), { replace: true });
        return;
      }
    }
    setSavedQuery(searchParams.toString());

    const { page, pageSize, filters: filtersFromUrl, searchFromUrl } = parseProjectsListParams(searchParams);

    setPagination(prev => ({ ...prev, page, pageSize }));
    setFilters(filtersFromUrl);
    setPendingFilters(filtersFromUrl);
    setSearchTerm(searchFromUrl);
    setSearchInput(searchFromUrl);

    const requestId = ++fetchRequestIdRef.current;

    const loadProjects = async () => {
      setLoading(true);
      try {
        const response: ProjectListResponse = await projectService.getProjectsPaginated(
          page,
          pageSize,
          filtersFromUrl
        );
        if (requestId !== fetchRequestIdRef.current) return;

        setProjects(response.data);
        setPagination(prev => ({
          ...prev,
          page,
          pageSize,
          totalCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
          hasNext: response.pagination.hasNext,
          hasPrevious: response.pagination.hasPrevious
        }));
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) return;
        console.error('Error fetching projects:', error);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    loadProjects();
  }, [searchParams]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Add debugging for modal state changes
  useEffect(() => {
    console.log('ProjectsView: Modal state changed:', {
      isModalVisible,
      selectedProject: selectedProject ? {
        id: selectedProject.id,
        name: selectedProject.name,
        type: selectedProject.type
      } : null
    });
  }, [isModalVisible, selectedProject]);

  const fetchFilterOptions = async () => {
    try {
      const options = await projectService.getProjectFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const updateUrlParams = (newPagination?: Partial<typeof pagination>, newFilters?: ProjectFilters) => {
    const params = new URLSearchParams(searchParams);
    
    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }
    
    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    
    setSearchParams(params);
  };

  const fetchProjects = async () => {
    const { page, pageSize, filters: filtersFromUrl } = parseProjectsListParams(searchParams);
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);
    try {
      const response: ProjectListResponse = await projectService.getProjectsPaginated(
        page,
        pageSize,
        filtersFromUrl
      );
      if (requestId !== fetchRequestIdRef.current) return;

      setProjects(response.data);
      setPagination(prev => ({
        ...prev,
        page,
        pageSize,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNext: response.pagination.hasNext,
        hasPrevious: response.pagination.hasPrevious
      }));

      if (pendingScrollAfterCreateRef.current) {
        pendingScrollAfterCreateRef.current = false;
        scrollListToTopAfterPaint(listScrollRef.current);
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error('Error fetching projects:', error);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAddProject = () => {
    setSelectedProject(undefined);
    setIsModalVisible(true);
  };

  const handleEditProject = (project: Project) => {
    console.log('ProjectsView: handleEditProject called with project:', {
      projectId: project.id,
      projectName: project.name,
      projectType: project.type,
      projectStatus: project.status
    });
    setSelectedProject(project);
    setIsModalVisible(true);
  };

  const handleSelectProject = (project: Project) => {
    const { page, pageSize, filters: currentFilters } = parseProjectsListParams(searchParams);
    navigate(`/projects/${project.id}`, {
      state: { projectsListSearch: buildProjectsListSearch(page, pageSize, currentFilters) }
    });
  };

  const toggleProjectTasks = async (projectId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (expandedProjectId === projectId) {
      // Collapse
      setExpandedProjectId(null);
    } else {
      // Expand - fetch tasks if not already loaded
      setExpandedProjectId(projectId);
      
      if (!projectTasks[projectId] || projectTasks[projectId].error) {
        // Set loading state
        setProjectTasks(prev => ({
          ...prev,
          [projectId]: { tasks: [], loading: true, error: null }
        }));
        
        try {
          // Fetch project with tasks - use smaller page size for faster loading
          // Only fetch first 50 tasks for summary view (can be paginated later if needed)
          const projectWithTasks = await projectService.getProjectById(projectId, 1, 50);
          setProjectTasks(prev => ({
            ...prev,
            [projectId]: { 
              tasks: projectWithTasks.tasks || [], 
              loading: false, 
              error: null,
              totalCount: projectWithTasks.taskPagination?.totalCount || (projectWithTasks.tasks?.length || 0),
              hasMore: (projectWithTasks.taskPagination?.totalCount || 0) > 50
            }
          }));
        } catch (error) {
          console.error('Error fetching project tasks:', error);
          setProjectTasks(prev => ({
            ...prev,
            [projectId]: { 
              tasks: [], 
              loading: false, 
              error: error instanceof Error ? error.message : 'Failed to load tasks' 
            }
          }));
        }
      }
    }
  };

  const handleSaveProject = async (projectData: Partial<Project>, h200FormFile?: File) => {
    try {
      const isNew = !selectedProject;
      let savedProject;
      
      if (selectedProject) {
        // Update existing project
        savedProject = await projectService.updateProject(selectedProject.id, projectData);
        
        // If there's an H200 form file to upload, handle it separately
        if (h200FormFile && projectData.type === 'H200') {
          try {
            await projectService.uploadH200Form(selectedProject.id, h200FormFile);
          } catch (uploadError) {
            console.error('Error uploading H200 form:', uploadError);
            // Continue with project update even if form upload fails
          }
        }
      } else {
        // Create new project
        savedProject = await projectService.createProject(projectData);
        
        // If there's an H200 form file to upload for a new project, handle it separately
        if (h200FormFile && projectData.type === 'H200' && savedProject.id) {
          try {
            await projectService.uploadH200Form(savedProject.id, h200FormFile);
          } catch (uploadError) {
            console.error('Error uploading H200 form:', uploadError);
            // Continue with project creation even if form upload fails
          }
        }
      }
      
      setIsModalVisible(false);
      if (isNew) {
        pendingScrollAfterCreateRef.current = true;
        const urlPage = parseInt(searchParams.get('page') || '1', 10);
        if (urlPage !== 1 || pagination.page !== 1) {
          updateUrlParams({ page: 1 }, filters);
        } else {
          await fetchProjects();
        }
      } else {
        await fetchProjects();
      }
    } catch (error) {
      console.error('Error saving project:', error);
      throw error;
    }
  };

  const handleCancelProject = async (reason: string) => {
    if (!projectToCancel) return;
    
    const cancelledName = projectToCancel.name;
    setCancelError(null);
    try {
      setLoading(true);
      
      await projectService.cancelProject(projectToCancel.id, reason);
      
      setIsCancelProjectModalVisible(false);
      setProjectToCancel(null);
      fetchProjects();
    } catch (error) {
      console.error('Error cancelling project:', error);
      setCancelError(describeActionError(error, `Couldn't cancel ${cancelledName ? `"${cancelledName}"` : 'the project'}. Try again.`));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    const newPendingFilters = { ...pendingFilters, [name]: value };
    setPendingFilters(newPendingFilters);
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    updateUrlParams({ page: 1 }, pendingFilters); // Reset to page 1 when applying filters
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page }, filters);
  };

  // Server-side sort (allowlisted columns) persisted in the URL, like the filters.
  const currentSortBy = searchParams.get('sortBy');
  const currentSortOrder: 'asc' | 'desc' = searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc';
  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    updateUrlParams({ page: 1 }, { ...filters, sortBy: key, sortOrder: dir });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateUrlParams({ pageSize, page: 1 }, filters);
  };

  // Search lives in the filter panel header (V2-077): Enter (or the clear "x") runs it, within the
  // applied filters (V2-076).
  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchTerm(trimmed);
    // Update URL params with trimmed search term
    const newFilters = { ...filters, search: trimmed };
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: ProjectFilters = {
      serviceArea: '',
      status: '',
      startDate: '',
      endDate: '',
      type: '',
      search: ''
    };
    setPendingFilters(clearedFilters);
    setFilters(clearedFilters);
    setSearchInput('');
    setSearchTerm('');
    updateUrlParams({ page: 1 }, clearedFilters);
  };

  // Check if there are any active filters
  // Only the filter fields count: sortBy/sortOrder are undefined at the defaults, and counting
  // them made the "Active" pill show on a fresh, unfiltered list (W-M1).
  // Search is not a filter: it has its own field and clear button in the panel header (V2-077).
  const hasActiveFilters = () => {
    const { serviceArea, status, type, startDate, endDate } = filters;
    return [serviceArea, status, type, startDate, endDate].some((value) => Boolean(value && String(value).trim()));
  };

  /** How many applied filters differ from their defaults - the collapsed filter bar's chip. */
  const activeFilterCount = () => {
    const { serviceArea, status, type, startDate, endDate } = filters;
    return [serviceArea, status, type, startDate, endDate].filter((value) => Boolean(value && String(value).trim())).length;
  };

  // Check if pending filters are different from applied filters
  const hasUnappliedChanges = () => {
    return pendingFilters.serviceArea !== filters.serviceArea ||
           pendingFilters.status !== filters.status ||
           pendingFilters.startDate !== filters.startDate ||
           pendingFilters.endDate !== filters.endDate ||
           pendingFilters.type !== filters.type ||
           pendingFilters.search !== filters.search;
  };

  // The nested tasks list inside an expanded project row. Its own DataTable (rather than a
  // hand-rolled <table>) so it gets the same phone card layout instead of widening the outer
  // card on a narrow screen — a project row expanded on a phone would otherwise nest one
  // horizontal scroller inside another.
  const buildProjectTaskColumns = (project: Project): DataTableColumn<any>[] => [
    {
      key: 'taskNumber',
      header: 'Task ID',
      width: '15%',
      mobile: 'title',
      cell: (task) => (
        // A label only (the row's eye button opens the project), so plain text, not a dark pill.
        task.taskNumber ? <IdText>{task.taskNumber}</IdText> : <EmptyValue />
      ),
    },
    {
      key: 'name',
      header: 'Name',
      width: '30%',
      mobile: 'subtitle',
      className: 'tw-break-words',
      cell: (task) => task.name || task.description || <EmptyValue />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '15%',
      align: 'center',
      mobile: 'trailing',
      // Default (task) vocabulary — this is a task status, not a project status.
      cell: (task) => <StatusBadge status={task.status} />,
    },
    {
      key: 'type',
      header: 'Type',
      width: '15%',
      align: 'center',
      cell: (task) => (
        <Badge pill bg={task.type === 'Quantified' ? 'info' : task.type === 'Routine' ? 'warning' : 'secondary'}>
          {task.type}
        </Badge>
      ),
    },
    {
      key: 'coding',
      header: (
        <InfoTooltip
          content={
            <div>
              <div><strong>Coding Formula:</strong></div>
              <div style={{ marginTop: '4px' }}>
                If a task belongs to a project that has a job number, the task is coded to the job number. Otherwise, the task is coded to its assigned activity number.
              </div>
              {projectTasks[project.id]?.tasks?.some((task: any) =>
                task.type === 'Custom' && !task.project?.jobNumber
              ) && (
                <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#ffc107' }}>
                  <strong>Note:</strong> For Custom tasks without job numbers, "ZZZ-ZZZZ" is used as a temporary coding number.
                </div>
              )}
            </div>
          }
          id={`coding-tooltip-projects-view-${project.id}`}
          placement="top"
          maxWidth="400px"
        >
          <span>
            Coding <i className="bi bi-info-circle tw-ml-1" style={{ fontSize: '0.85rem', opacity: 0.7 }}></i>
          </span>
        </InfoTooltip>
      ),
      width: '15%',
      align: 'center',
      cell: (task) => {
        // If task has a project with job number, display job number; otherwise the activity number.
        if (task.project?.jobNumber) {
          // Codes are plain text, not pills (W-M5).
          return <IdText>{task.project.jobNumber}</IdText>;
        } else if (task.activities && task.activities.length > 0) {
          return <IdText>{task.activities[0].activityNumber}</IdText>;
        }
        return <EmptyValue />;
      },
    },
    {
      key: 'activity',
      header: 'Activity',
      width: '15%',
      align: 'center',
      cell: (task) =>
        task.activities && task.activities.length > 0 ? (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {task.activities.map((activity: any, index: number) => (
              <span key={activity.id || index} className="tw-text-sm">
                {activity.activityNumber} - {activity.activityName}
              </span>
            ))}
          </div>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '10%',
      align: 'right',
      mobile: 'trailing',
      cell: (task) => (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleSelectProject(project);
          }}
          title="View Task Details"
        >
          <i className="bi bi-eye"></i>
        </Button>
      ),
    },
  ];

  // Expanded-row content for a project: its tasks, with the same loading/error/"first 50"
  // affordances the inline table used to render directly in the <tr>.
  const renderProjectTasksExpanded = (project: Project) => {
    const state = projectTasks[project.id];
    return (
      <div className="tw-p-3" onClick={(e) => e.stopPropagation()}>
        {state?.loading ? (
          <div className="tw-text-center tw-py-3">
            <Spinner size="sm" className="tw-mr-2">Loading...</Spinner>
            Loading tasks...
          </div>
        ) : state?.error ? (
          <Alert variant="danger" className="tw-mb-0">
            <i className="bi bi-exclamation-triangle tw-mr-2"></i>
            {state.error}
          </Alert>
        ) : state?.tasks && state.tasks.length > 0 ? (
          <div>
            {state.hasMore && (
              <Alert variant="info" className="tw-mb-2 !tw-py-2">
                <i className="bi bi-info-circle tw-mr-2"></i>
                Showing first 50 tasks. Total: {state.totalCount} tasks.
              </Alert>
            )}
            <DataTable
              columns={buildProjectTaskColumns(project)}
              data={state.tasks}
              rowKey={(task) => task.id}
              size="sm"
              hover
              emptyMessage="No tasks found for this project"
            />
          </div>
        ) : (
          <div className="tw-text-center tw-text-content-muted tw-py-3">
            <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
            No tasks found for this project
          </div>
        )}
      </div>
    );
  };

  // One column config drives both the desktop table and the phone stacked-card layout.
  // Project # is the card title, Name the secondary line under it, and Status + the row's
  // expander/kebab sit top-right as "trailing" (matching the Work IDs and Service Areas lists).
  const projectColumns: DataTableColumn<Project>[] = [
    {
      // V2-072 / ACT-04: the expander leads the row in its own narrow column, before Project #.
      key: 'expand',
      header: <span className="tw-sr-only">Expand</span>,
      mobile: 'trailing',
      width: EXPAND_COL_WIDTH,
      cell: (project) => (
        <RowExpandToggle
          expanded={expandedProjectId === project.id}
          onToggle={() => toggleProjectTasks(project.id)}
          label={expandedProjectId === project.id ? 'Hide tasks' : 'Show tasks'}
        />
      ),
    },
    {
      key: 'projectNumber',
      header: 'Project #',
      sortable: true,
      mobile: 'title',
      width: '10%',
      cell: (project) => (
        // The shared ID-link style (W-L10): blue, body size, underline on hover only.
        project.projectNumber ? (
          <IdLink onClick={() => handleSelectProject(project)}>{project.projectNumber}</IdLink>
        ) : (
          <EmptyValue />
        )
      ),
    },
    {
      key: 'jobNumber',
      header: 'Job #',
      sortable: true,
      width: '10%',
      cell: (project) =>
        project.jobNumber ? (
          <span>{project.jobNumber}</span>
        ) : project.type !== 'Internal' && project.type !== 'Quantified' && project.type !== 'Routine' ? (
          // Only show "Missing" if project type is NOT Internal, Quantified, or Routine
          <Badge pill bg="danger">Missing</Badge>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      mobile: 'subtitle',
      // A long project name wraps to at most 2 lines (full name in the tooltip) in a column wide
      // enough to read it, instead of setting the row height (W-M7).
      cell: (project) =>
        project.name ? (
          <div className="md:tw-min-w-[16rem] md:tw-max-w-[28rem]">
            <ClampText>{project.name}</ClampText>
          </div>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      width: '15%',
      cell: (project) => (
        // Type is not a status — its own colours (H200/Quantified/Routine/other), kept as-is.
        // tw-whitespace-nowrap added: this badge was clipping mid-word in a narrow column.
        <Badge
          pill
          bg={project.type === 'H200' ? 'primary' : project.type === 'Quantified' ? 'info' : project.type === 'Routine' ? 'warning' : 'secondary'}
        >
          {project.type}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      width: '15%',
      cell: (project) => (
        <StatusBadge status={project.status} vocabulary="project" label={formatProjectStatus(project.status)} soft />
      ),
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      width: '10%',
      cell: (project) => <ServiceAreaPill value={project.serviceArea ?? null} compact />,
    },
    {
      key: 'startDate',
      header: 'Start Date',
      sortable: true,
      width: '10%',
      cell: (project) => formatDateForDisplay(project.startDate),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      width: ACTIONS_COL_WIDTH,
      cell: (project) => (
        // V2-072: only the ⋮ menu here; the expander leads the row.
        <RowActions
          actions={[
            {
              kind: 'edit',
              label: 'Edit Project',
              onClick: () => handleEditProject(project),
              hidden: !canEditProject,
            },
            {
              kind: 'cancel',
              label: 'Cancel Project',
              onClick: () => {
                setProjectToCancel(project);
                setIsCancelProjectModalVisible(true);
              },
              hidden: !(canCancelProject && project.status !== 'cancelled'),
            },
          ]}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <ListPage>
        <div className="tw-flex tw-grow tw-items-center tw-justify-center">
          <Spinner>Loading...</Spinner>
        </div>
      </ListPage>
    );
  }

  return (
    // The shared list layout (W-H5): header, filters and search keep their height; the table box
    // takes all the rest and is the ONE scroll area (sticky header, pinned Project # and Actions).
    <ListPage className="projects-page">
      <PageHeader
        title="Projects"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => {
              setLoading(true);
              fetchProjects().finally(() => setLoading(false));
            },
          },
          {
            key: 'export',
            label: 'Export',
            icon: 'bi-download',
            intent: 'neutral',
            onClick: () => console.log('Export projects'),
          },
          {
            key: 'add',
            label: 'Add Project',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateProject,
            onClick: handleAddProject,
          },
        ]}
      />

      <ProjectFiltersComponent
          serviceAreas={SERVICE_AREAS}
          filters={pendingFilters}
          projects={projects}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          showApplyButton={true}
          onApply={handleApplyFilters}
          applyDisabled={!hasUnappliedChanges()}
          hasActiveFilters={hasActiveFilters()}
          activeFilterCount={activeFilterCount()}
          // No filter hides projects by default, so the applied count is the changed count.
          appliedFilterCount={activeFilterCount()}
          search={{
            value: searchInput,
            onChange: setSearchInput,
            onSearch: handleSearch,
            placeholder: 'Project name, number, description or task',
            label: 'Search projects',
          }}
        />

      {/* No wrapper box between the body and the table: the DataTable's own box is the scroller,
          so the sticky header attaches to it (an extra wrapper used to catch it). */}
      {cancelError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setCancelError(null)} className="tw-mb-2">
          {cancelError}
        </Alert>
      )}

      <ListPage.Body ref={listScrollRef}>
        <DataTable
          columns={projectColumns}
          data={projects}
          rowKey={(project) => project.id}
          hover
          fill
          onRowClick={handleSelectProject}
          renderExpanded={renderProjectTasksExpanded}
          isExpanded={(project) => expandedProjectId === project.id}
          sortBy={currentSortBy}
          sortDir={currentSortOrder}
          onSort={handleSort}
          emptyMessage={
            <>
              <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
              No projects found
            </>
          }
        />
      </ListPage.Body>

      {/* Server-side pagination */}
      <ServerPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          className="tw-mt-2"
        />

      <ProjectModal
        visible={isModalVisible}
        onClose={() => {
          console.log('ProjectsView: Closing modal');
          setIsModalVisible(false);
        }}
        onSave={handleSaveProject}
        project={selectedProject}
      />

      <CancelProjectConfirmationModal
        visible={isCancelProjectModalVisible}
        onClose={() => {
          setIsCancelProjectModalVisible(false);
          setProjectToCancel(null);
        }}
        onConfirm={handleCancelProject}
        projectName={projectToCancel?.name}
      />
    </ListPage>
  );
};

export default ProjectsView; 